import { ILessonListItem } from '@lb/api/mylb/interfaces';
import { isAfter, isBefore } from 'date-fns';
import { getLessonTypeVariant } from './lesson-type-colors.util';

/**
 * Whether the viewer may take instructor/admin actions on the lesson, per the backend's `canEdit`.
 * Only an explicit `false` restricts: a lesson from a source that doesn't compute the flag stays
 * editable in the UI, and the API still refuses anything the viewer may not do.
 */
export function isLessonEditable(lesson: Pick<ILessonListItem, 'canEdit'> | null | undefined): boolean {
	return lesson?.canEdit !== false;
}

// isAfter(this._currentTime(), this.lesson().lessonDate) && this.lesson().attendanceStatusID == null);
export function canTakeAttendance(lesson: ILessonListItem, currentTime: Date | null = null): boolean {
	if (currentTime === null) {
		currentTime = new Date();
	}

	return isLessonEditable(lesson) && isAfter(currentTime, lesson.lessonDate) && lesson.attendanceStatusID == null;
}

export function canRescheduleLesson(
	experience: 'instructor' | 'learner' | 'history' | null,
	lesson: ILessonListItem,
	currentTime: Date | null = null
): boolean {
	if (currentTime === null) {
		currentTime = new Date();
	}

	if (experience === 'instructor') {
		// A lesson from another instructor's era (a predecessor or substitute) is view-only.
		// 4 = Awaiting Payment, 5 = Completed
		return isLessonEditable(lesson) && ![4, 5].includes(lesson.lessonStatusID);
	}

	if (experience === 'learner') {
		// A chirp room lesson is a group session on the room's shared schedule — a member attends
		// it but never controls its timing, so rescheduling is instructor/admin-only. Room lessons
		// are identified by their attendance mode (D16), not just the chirp lesson type, since a
		// room can schedule lessons of any type.
		if (lesson.isMultiAttendance || getLessonTypeVariant(lesson.lessonTypeID) === 'chirp') {
			return false;
		}

		// 4 = Awaiting Payment, 5 = Completed
		if ([4, 5].includes(lesson.lessonStatusID)) {
			return false;
		}

		const lessonStart = new Date(lesson.lessonDate);

		if (isBefore(lessonStart, currentTime)) {
			return false;
		}

		// Learner cannot reschedule within 24 hours of the lesson
		const twentyFourHoursFromNow = new Date(currentTime.getTime() + 24 * 60 * 60 * 1000);
		if (isBefore(lessonStart, twentyFourHoursFromNow)) {
			return false;
		}

		return true;
	}

	return false;
}
